import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

/**
 * Time calculation utilities for StoreCheck
 * All business logic for time calculations should be here
 */

/**
 * Format a StoreCheck date for display.
 * StoreCheck timestamps are stored as "Venezuela local time in the UTC slot"
 * (the model pre-save hook subtracts 4 h before saving), so we read the raw
 * UTC value without any further timezone conversion.
 */
export const fmtStoreCheck = (d, fmt = 'DD/MM/YYYY HH:mm') =>
    d ? dayjs.utc(d).format(fmt) : '—';

/**
 * Format a generic/audit date for display.
 * Audit and other models store real UTC timestamps, so we shift by -4 h
 * to render in Venezuela time (UTC-4, no DST).
 */
export const fmtVZ = (d, fmt = 'DD/MM/YYYY HH:mm') =>
    d ? dayjs.utc(d).subtract(4, 'hour').format(fmt) : '—';

/**
 * Format a Levantamiento `capturedAt` in Venezuela time, accounting for mixed provenance:
 * mary rows come from StoreCheck (already pre-shifted to VZ, read raw), while auditor and
 * competencia come from real-UTC models (shift −4h). Anything else defaults to the UTC shift.
 */
export const fmtCapture = (d, source, fmt = 'DD/MM/YYYY HH:mm') =>
    source === 'mary' ? fmtStoreCheck(d, fmt) : fmtVZ(d, fmt);

/**
 * Calculate audit duration in minutes with validation
 * Uses createdAt as fallback for endTime when endTime is missing
 * 
 * @param {Object} storeCheck - StoreCheck object with startTime, endTime, createdAt
 * @returns {number} Duration in minutes (0-480 range, or 0 if invalid)
 */
export function calculateAuditDuration(storeCheck) {
    if (!storeCheck) return 0;
    
    const startTimeValue = storeCheck.startTime;
    const endTimeValue = storeCheck.endTime || storeCheck.createdAt; // Fallback to createdAt
    
    if (!startTimeValue || !endTimeValue) return 0;
    
    const start = dayjs(startTimeValue);
    const end = dayjs(endTimeValue);
    
    if (!start.isValid() || !end.isValid()) return 0;
    
    const diff = end.diff(start, 'minutes');
    
    // Sanity check: audit should be between 0 and 480 minutes (8 hours max)
    return (diff >= 0 && diff <= 480) ? diff : 0;
}

/**
 * Calculate average audit time from an array of store checks
 * 
 * @param {Array} storeChecks - Array of StoreCheck objects
 * @returns {number} Average duration in minutes
 */
export function calculateAverageAuditTime(storeChecks) {
    if (!storeChecks || storeChecks.length === 0) return 0;
    
    let validCount = 0;
    const totalTime = storeChecks.reduce((sum, check) => {
        const duration = calculateAuditDuration(check);
        if (duration > 0) {
            validCount++;
            return sum + duration;
        }
        return sum;
    }, 0);
    
    return validCount > 0 ? Math.round(totalTime / validCount) : 0;
}

/**
 * Calculate total audit time from an array of store checks
 * 
 * @param {Array} storeChecks - Array of StoreCheck objects
 * @returns {number} Total duration in minutes
 */
export function calculateTotalAuditTime(storeChecks) {
    if (!storeChecks || storeChecks.length === 0) return 0;
    
    return storeChecks.reduce((sum, check) => {
        return sum + calculateAuditDuration(check);
    }, 0);
}

/**
 * Determine if duration is considered "long" (warning threshold)
 * 
 * @param {number} minutes - Duration in minutes
 * @returns {boolean} True if duration exceeds warning threshold
 */
export function isDurationWarning(minutes) {
    return minutes > 30;
}

/**
 * Format duration for display
 * 
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 */
export function formatDuration(minutes) {
    if (minutes <= 0) return '--';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

